import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import nammaLogo from "../Images/Namma/NammaLogo.png";

const NAV_LINKS = [
  { label: "Experience", path: "#experience" },
  { label: "Reker",      path: "https://reker.world", external: true },
  { label: "Blog",       path: "/blog" },
];

const Navbar: React.FC = () => {
  const [scrolled, setScrolled]         = useState(false);
  const [menuOpen, setMenuOpen]         = useState(false);
  const [activeSection, setActiveSection] = useState<string>("");
  const navigate  = useNavigate();
  const location  = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Track which section is visible
  useEffect(() => {
    const sections = ["experience", "what"];
    const observers: IntersectionObserver[] = [];

    sections.forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveSection(id); },
        { threshold: 0.3 }
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach(o => o.disconnect());
  }, [location.pathname]);

  useEffect(() => { setMenuOpen(false); }, [location]);

  const go = (path: string, external?: boolean) => {
    if (external) { window.open(path, "_blank"); setMenuOpen(false); return; }
    if (path.startsWith("#")) {
      const id = path.slice(1);
      if (location.pathname !== "/" && location.pathname !== "/nammapass") {
        navigate("/nammapass");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(path);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    setMenuOpen(false);
  };

  const isActive = (path: string) => {
    if (path === "/blog") return location.pathname.startsWith("/blog");
    if (path.startsWith("#")) return !location.pathname.startsWith("/blog") && activeSection === path.slice(1);
    return false;
  };

  return (
    <nav className={`np-nav ${scrolled ? "scrolled" : ""}`}>

      {/* ── Logo ── */}
      <button className="np-nav-logo-btn" onClick={() => go("/nammapass")} aria-label="Home">
        <img src={nammaLogo} alt="Namma Pass" className="np-logo-nav" />
      </button>

      {/* ── Centre pill ── */}
      <ul className="np-nav-links">
        {NAV_LINKS.map(({ label, path, external }) => (
          <li key={label}>
            <button
              className={`np-nav-link-btn ${!external && isActive(path) ? "active" : ""}`}
              onClick={() => go(path, external)}
            >
              {label}
              {external && (
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none"
                  style={{ marginLeft: 4, opacity: 0.5 }}>
                  <path d="M1 9L9 1M9 1H3M9 1V7" stroke="currentColor" strokeWidth="1.4"
                    strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          </li>
        ))}
      </ul>



      {/* ── Mobile hamburger ── */}
      <button
        className="np-nav-mobile-btn"
        aria-label="Toggle menu"
        onClick={() => setMenuOpen(o => !o)}
      >
        <div className={`np-hamburger ${menuOpen ? "open" : ""}`}>
          <span /><span /><span />
        </div>
      </button>

      {/* ── Mobile drawer ── */}
      {menuOpen && (
        <div className="np-nav-drawer">
          {NAV_LINKS.map(({ label, path, external }) => (
            <button key={label} className="np-nav-drawer-item"
              onClick={() => go(path, external)}>
              {label}
              {external && (
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none"
                  style={{ marginLeft: 6, opacity: 0.4 }}>
                  <path d="M1 9L9 1M9 1H3M9 1V7" stroke="currentColor" strokeWidth="1.4"
                    strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          ))}
          <a
            className="np-nav-cta np-nav-drawer-cta"
            href="https://play.google.com/store/apps/details?id=reker.example.evoltech&pcampaignid=web_share"
            target="_blank"
            rel="noopener noreferrer"
          >
            Get the App
          </a>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
